import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, useMediaQuery, useTheme, Button } from '@mui/material';
import GuestDetails from './Components/GuestDetials';
import ReservationDetails from './Components/ReservationDetails';
import Check_IN_Out from './Components/Check_IN_Out';
import CustomCard from '../../components/UIComponents/CustomCard';
import HeaderComponent from '../../components/Header';
import { useLoading } from "../../context/loading.context";
import { useNotification } from '../../context/notification.context';
import { createGuestandReservation, estimatetariff, getDocInfoComplete, getFileDetails, getReservationDetails, getUnallocatedAmount, updateFileAttachment, updateReservation, } from '../../service/network';
import { useLocation, useNavigate } from 'react-router-dom';
import AvailedServices from './Components/AvailedSeverice';
import PaymentDetails from './Components/PaymentDetails';
import { Masonry } from '@mui/lab';
import axios from 'axios';
import moment from 'moment';

export default function Reservations() {
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    const { showLoader, hideLoader } = useLoading();
    const { showNotification } = useNotification();
    const location = useLocation();
    const navigate = useNavigate();
    const [unallocatedAmount, setUnallocatedAmount] = useState(0);

    const [isEditMode, setIsEditMode] = useState(false);
    const [reservationId, setReservationId] = useState(null);
    const [rawReservation, setRawReservation] = useState({});
    const [estimates, setEstimates] = useState(null);
    const [guests, setGuests] = useState([]);
    const [reservation, setReservation] = useState({
        booking_source: '',
        room: '',
        room_type: '',
        from_date: moment().format("YYYY-MM-DD"),
        to_date: moment().add(1, 'day').format("YYYY-MM-DD"),
        purpose: '',
        no_of_child: 0,
        no_of_adult: 1,
        no_of_days: "",
        status: 'Booked',
        type: '',
        company: '',
        invoiced_amount: 0,
        paid_amount: 0
    });

    // File upload function
    const uploadFileForGuest = async (file, docname) => {
        showLoader(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('doctype', 'Guest Details');
            formData.append('docname', docname);
            formData.append('folder', 'Home/Attachments');
            formData.append('is_private', '1');
            formData.append('fieldname', 'id_proof_attachment'); // optional, only if you use it in the DocType

            const response = await uploadFrappeMedia(formData);

            if (response?.data?.message?.file_url) {
                return response.data.message.file_url;
            }

            throw new Error('File upload failed');
        } catch (error) {
            console.error('File upload error:', error);
            showNotification('error', 'Upload Failed', error.message || 'Failed to upload file');
            throw error;
        } finally {
            hideLoader();
        }
    };

    // Handle file upload for a guest
    const handleGuestFileUpload = async (guestId, file) => {
        try {
            const guest = guests.find(g => g.id === guestId);
            if (!guest) return;

            const fileUrl = await uploadFileForGuest(file, guest.guest_name || guest.id);

            setGuests(prevGuests =>
                prevGuests.map(g =>
                    g.id === guestId
                        ? { ...g, idProofAttachment: fileUrl, file: null, preview: null }
                        : g
                )
            );

            return fileUrl;
        } catch (error) {
            console.error('Error uploading guest file:', error);
            throw error;
        }
    };

    // Handle file change for a guest
    const handleFileChange = async (e, guestId) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file
        const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
        if (!validTypes.includes(file.type)) {
            showNotification('error', 'Invalid File', 'Only JPG/PNG/PDF files are allowed');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            showNotification('error', 'File Too Large', 'File size should be less than 5MB');
            return;
        }

        // Create preview for images
        let preview = null;
        if (file.type.startsWith('image/')) {
            preview = URL.createObjectURL(file);
        }

        // Update guest with file and preview
        setGuests(prevGuests =>
            prevGuests.map(g =>
                g.id === guestId
                    ? { ...g, file, preview }
                    : g
            )
        );

        try {
            await handleGuestFileUpload(guestId, file);
        } catch (error) {
            // Error handling is done in uploadFileForGuest
        }
    };

    useEffect(() => {
        if (rawReservation) {
            estimatetariff({ reservation_id: rawReservation.name }).then((resp) => {
                setEstimates(resp.data.message);
            }).catch((err) => {
                console.log(err);
            });
        }
    }, [rawReservation]);

    // Handle adding a new guest
    const handleAddGuest = (newGuest) => {
        setGuests([...guests, {
            ...newGuest,
            id: `temp_${Date.now()}`
        }]);
    };

    // Handle reservation changes
    const handleReservationChange = (field, value) => {
        if (field === 'no_of_child' || field === 'no_of_adult') {
            value = Number(value) || (field === 'no_of_adult' ? 1 : 0);
        }

        setReservation(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const getGuestDisplayName = (guest, index) => {
        const name = `${guest.firstName || ''} ${guest.lastName || ''}`.trim();
        return name || guest.guest_name || `Guest ${index + 1}`;
    };

    const validateGuests = () => {
        const missingNames = [];
        let primaryMissingMobile = false;
        let primaryMissingEmail = false;

        guests.forEach((guest, index) => {
            const hasName = Boolean((guest.firstName || '').trim() || (guest.lastName || '').trim());
            if (!hasName) {
                missingNames.push(getGuestDisplayName(guest, index));
            }

            // Attachments are optional

            if (index === 0) {
                if (!guest.mobile || !guest.mobile.trim()) {
                    primaryMissingMobile = true;
                }
                if (!guest.email || !guest.email.trim()) {
                    primaryMissingEmail = true;
                }
            }
        });

        if (missingNames.length === 0 && !primaryMissingMobile && !primaryMissingEmail) {
            return true;
        }

        const issues = [];
        if (missingNames.length > 0) {
            issues.push(`Missing name for: ${missingNames.join(', ')}`);
        }
        if (primaryMissingMobile || primaryMissingEmail) {
            const primaryFields = [];
            if (primaryMissingMobile) primaryFields.push('mobile');
            if (primaryMissingEmail) primaryFields.push('email');
            issues.push(`Primary guest missing: ${primaryFields.join(' & ')}`);
        }

        showNotification('error', 'Guest Details Required', issues.join('. '));
        return false;
    };

    const fetchReservationData = async () => {
        if (location.state?.reservationId) {
            try {
                showLoader(true);
                const id = location.state.reservationId;
                setReservationId(id);
                setIsEditMode(true);

                const response = await getReservationDetails(id);
                const resData = response.data.data;
                setRawReservation(resData);

                // Update reservation state
                setReservation({
                    booking_source: resData.booking_source || '',
                    name: resData.name || '',
                    room: resData.room || '',
                    room_type: resData.room_type || '',
                    from_date: resData.from_date,
                    to_date: resData.to_date,
                    purpose: resData.purpose || '',
                    no_of_child: Number(resData.no_of_child) || 0,
                    no_of_adult: Number(resData.no_of_adult) || 1,
                    no_of_days: resData.no_of_days || "",
                    status: resData.status || '',
                    type: resData.reservation_type || '',
                    company: resData.company || '',
                    package: resData.package || null,
                    corporate: resData.corporate || null,
                    paid_amount: resData.paid_amount || 0,
                    invoiced_amount: resData.invoiced_amount || 0
                });

                // Update guests
                if (resData.guest_details && resData.guest_details.length > 0) {
                    let guestData = [];


                    for (let guest of resData.guest_details) {

                        let docinfogst = await getDocInfoComplete({
                            doctype: "Guest Details",
                            name: guest.guest_name
                        });

                        let attchs = docinfogst.data.docinfo?.attachments || [];
                        let guest_attachments = [];

                        for (let atc of attchs) {
                            let deets = await getFileDetails(atc.name);
                            if (deets.data.data) {
                                guest_attachments.push({
                                    ...deets.data.data,
                                    type: deets.data.data._user_tags?.split(",")[1],
                                    fileUrl: deets.data.data.file_url,
                                    fileName: deets.data.data.file_name,
                                    fileType: deets.data.data.file_type,
                                    fileSize: deets.data.data.file_size,
                                    format: deets.data.data.file_type,
                                    fileId: deets.data.data.name,
                                });
                            }
                        }

                        guestData.push({
                            id: guest.name,
                            guest_name: guest.guest_name || '',
                            title: guest.title || '',
                            firstName: guest.first_name || '',
                            lastName: guest.last_name || '',
                            mobile: guest.mobile_number || '',
                            email: guest.email || '',
                            gender: guest.gender || '',
                            nationality: guest.nationality || '',
                            idProofType: guest.id_proof || '',
                            idNumber: guest.id_proof_number || '',
                            address: guest.address || '',
                            country: guest.country || '',
                            state: guest.state || '',
                            city: guest.city || '',
                            pinCode: guest.pin_code || '',
                            idProofAttachment: guest.id_proof_attachment || null,
                            attachments: guest_attachments
                        });
                    }
                    setGuests(guestData);
                }
            } catch (error) {
                console.error('Error fetching reservation details:', error);
                showNotification('error', 'Error', 'Failed to fetch reservation details');
            } finally {
                hideLoader();
            }
        }
    };

    useEffect(() => {
        fetchReservationData();
    }, [location.state?.reservationId]);

    // Prepare guest details for API
    const prepareGuestDetails = () => {
        return guests.map(guest => ({
            doctype: "Reservation Guest Details",
            name: guest.id.startsWith('temp_') ? undefined : guest.id,
            guest_name: guest.guest_name || '',
            title: guest.title,
            gender: guest.gender,
            id_proof: guest.idProofType,
            id_proof_number: guest.idNumber,
            id_proof_attachment: guest.idProofAttachment || null,
            country: guest.country,
            first_name: guest.firstName,
            last_name: guest.lastName,
            mobile_number: guest.mobile,
            email: guest.email,
            nationality: guest.nationality,
            address: guest.address,
            state: guest.state,
            city: guest.city,
            pin_code: guest.pinCode,
            attachments: guest.attachments?.map(att => ({
                file_url: att.fileUrl,
                file_name: att.fileName,
                file_type: att.fileType
            })) || []
        }));
    };

    // Submit both guest and reservation data
    const handleSubmitReservation = async () => {
        try {
            showLoader(true);

            // Basic validation
            if (!reservation.from_date || !reservation.to_date) {
                throw new Error('Check-in and check-out dates are required');
            }

            if (guests.length === 0) {
                throw new Error('At least one guest is required');
            }

            if (!validateGuests()) {
                return;
            }

            // Upload any pending files first
            // for (const guest of guests) {
            //     if (guest.file) {
            //         try {
            //             await handleGuestFileUpload(guest.id, guest.file);
            //         } catch (err) {
            //             console.warn(`Failed to upload file for guest ${guest.firstName}:`, err);
            //         }
            //     }
            // }

            if (isEditMode) {
                await handleUpdateReservation();
                return;
            }

            const guestDetails = prepareGuestDetails();

            const payload = {
                doc: JSON.stringify({
                    doctype: "Reservation",
                    ...reservation,
                    guest_details: guestDetails
                }),
                action: "Save"
            };

            const response = await createGuestandReservation(payload);

            if (response?.status === 200 || response?.data?.message || response?.data?.data) {
                showNotification('success', 'Success', 'Reservation created successfully');
                //Check the response for the new guests and the attached files

                try {
                    let respdtls = response.data.docs[0]
                    for (let gst in guests) {
                        if (guests[gst].attachments && (guests[gst].attachments.length > 0)) {
                            for (let fle of guests[gst].attachments) {
                                if (!fle.attached_to_doctype) {
                                    try {
                                        const attachResponse = await updateFileAttachment({
                                            fileName: fle.name,
                                            attached_to_doctype: "Guest Details",
                                            attached_to_name: respdtls.guest_details[gst].guest_name
                                        });

                                        if (!attachResponse.data?.data) {
                                            console.warn('File attachment to guest failed');
                                            showNotification('warning', 'Warning', 'File uploaded but attachment to guest failed');
                                        }
                                    } catch (error) {
                                        console.error('Error attaching file:', error);
                                        showNotification('error', 'Attachment Failed', 'Failed to link file to guest');
                                    }
                                }
                            }
                        }
                    }

                } catch (err) {
                    console.log(err);
                }

                navigate('/list/reservation');

                // Reset form
                setGuests([]);
                setReservation({
                    booking_source: '',
                    room: '',
                    room_type: '',
                    from_date: moment().format("YYYY-MM-DD"),
                    to_date: moment().add(1, 'day').format("YYYY-MM-DD"),
                    purpose: '',
                    no_of_child: 0,
                    no_of_adult: 2,
                    no_of_days: 1,
                    status: '',
                    room_type: '',
                    company: '',
                    package: null,
                    corporate: null,
                    invoiced_amount: 0,
                    paid_amount: 0
                });
            } else {
                throw new Error(response?.data?.message || 'Failed to create reservation');
            }
        } catch (error) {
            console.error('Error creating reservation:', error);
            let errorMessage = 'Failed to create reservation';

            if (error.response?.data?._server_messages) {
                try {
                    const messages = JSON.parse(error.response.data._server_messages);
                    errorMessage = messages.map(msg => {
                        try {
                            const parsed = JSON.parse(msg);
                            return parsed.message.replace(/<[^>]+>/g, '');
                        } catch {
                            return msg;
                        }
                    }).join(', ');
                } catch (parseError) {
                    console.error('Error parsing server messages:', parseError);
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            showNotification('error', 'Error', errorMessage);
        } finally {
            hideLoader();
        }
    };

    // Handle update reservation
    const handleUpdateReservation = async () => {
        try {
            showLoader(true);

            if (guests.length === 0) {
                throw new Error('At least one guest is required');
            }

            if (!validateGuests()) {
                return;
            }

            const guestDetails = prepareGuestDetails();

            const updatePayload = {
                name: reservationId,
                booking_source: reservation.booking_source,
                room: reservation.room,
                room_type: reservation.room_type,
                from_date: reservation.from_date,
                to_date: reservation.to_date,
                purpose: reservation.purpose,
                no_of_child: reservation.no_of_child,
                no_of_adult: reservation.no_of_adult,
                no_of_days: reservation.no_of_days,
                status: reservation.status,
                reservation_type: reservation.type,
                company: reservation.company,
                package: reservation.package,
                guest_details: guestDetails,
                corporate: reservation.corporate
            };

            const response = await updateReservation(reservationId, updatePayload);

            if (response?.status === 200 || response?.data?.message || response?.data?.data) {

                try {
                    let respdtls = response.data.data
                    for (let gst in guests) {
                        if (guests[gst].attachments && (guests[gst].attachments.length > 0)) {
                            for (let fle of guests[gst].attachments) {
                                if (!fle.attached_to_doctype) {
                                    try {
                                        const attachResponse = await updateFileAttachment({
                                            fileName: fle.name,
                                            attached_to_doctype: "Guest Details",
                                            attached_to_name: respdtls.guest_details[gst].guest_name
                                        });

                                        if (!attachResponse.data?.data) {
                                            console.warn('File attachment to guest failed');
                                            showNotification('warning', 'Warning', 'File uploaded but attachment to guest failed');
                                        }
                                    } catch (error) {
                                        console.error('Error attaching file:', error);
                                        showNotification('error', 'Attachment Failed', 'Failed to link file to guest');
                                    }
                                }
                            }
                        }
                    }

                } catch (err) {
                    console.log(err);
                }

                showNotification('success', 'Success', 'Reservation updated successfully');
                // navigate('/list/reservation');
                window.location.reload();
                // Refresh data to get updated guest names/IDs
                // const updatedRes = await getReservationDetails(reservationId);
                // const serverGuests = updatedRes?.data?.data?.guest_details || [];

                // const updatedGuests = guests.map(g => {
                //     const serverGuest = serverGuests.find(sg =>
                //         g.id?.startsWith('temp_')
                //             ? sg.first_name === g.firstName && sg.last_name === g.lastName
                //             : sg.name === g.id
                //     ) || {};

                //     return {
                //         ...g,
                //         id: serverGuest.name || g.id,
                //         guest_name: serverGuest.guest_name || g.guest_name,
                //     };
                // });

                // setGuests(updatedGuests);
            } else {
                throw new Error(response?.data?.message || 'Failed to update reservation');
            }
        } catch (error) {
            console.error('Error updating reservation:', error);
            let errorMessage = 'Failed to update reservation';

            if (error.response?.data?._server_messages) {
                try {
                    const messages = JSON.parse(error.response.data._server_messages);
                    errorMessage = messages.map(msg => {
                        try {
                            const parsed = JSON.parse(msg);
                            return parsed.message.replace(/<[^>]+>/g, '');
                        } catch {
                            return msg;
                        }
                    }).join(', ');
                } catch (parseError) {
                    console.error('Error parsing server messages:', parseError);
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            showNotification('error', 'Error', errorMessage);
        } finally {
            hideLoader();
        }
    };

    const handleGuestChange = (guestId, field, value) => {
        setGuests(prevGuests =>
            prevGuests.map(guest =>
                guest.id === guestId ? { ...guest, [field]: value } : guest
            )
        );
    };

 const gotoPayments = () => {
    navigate('/payment', {
        state: { reservationId: rawReservation?.name, details: location?.state?.details }
    });
};


    // Add this useEffect to fetch unallocated amount when reservation changes
    useEffect(() => {
        if (rawReservation?.name) {
            getUnallocatedAmount({ reservation: rawReservation.name })
                .then((res) => {
                    setUnallocatedAmount(res.data.message.unallocated);
                })
                .catch((err) => {
                    console.error('Error fetching unallocated amount:', err);
                    setUnallocatedAmount(0);
                });
        }
    }, [rawReservation?.name]);

    return (
        <Box sx={{ width: '100%', p: 2 }}>
            <HeaderComponent title={isEditMode ? "Update Reservation" : "Reservation"} />
            <CustomCard
                bgColor="light"
                shadow="lg"
                borderRadius="xxl"
                sx={{
                    width: '100%',
                    borderTopLeftRadius: 0,
                    minHeight: 800,
                    p: { xs: 2, sm: 3 },
                    overflow: 'hidden',
                }}
            >
                <Masonry columns={{ xs: 1, sm: 2, md: 3 }} spacing={2}>
                    <GuestDetails
                        guests={guests}
                        onAddGuest={handleAddGuest}
                        isEditMode={isEditMode}
                        onGuestChange={handleGuestChange}
                        onFileChange={handleFileChange}
                    />

                    <ReservationDetails
                        reservation={reservation}
                        onChange={handleReservationChange}
                        onSubmit={handleSubmitReservation}
                        isEditMode={isEditMode}
                    />

                    <Check_IN_Out
                        reservation={reservation}
                        tariff={estimates}
                        details={rawReservation}
                        confirmationStatus={reservation?.confirmation_status?.[0]}
                        unallocatedAmount={unallocatedAmount} // And pass it here

                    />

                    <AvailedServices
                        showDateField={false}
                        details={rawReservation}
                        services={rawReservation?.addon_services}
                        refresh={() => { fetchReservationData(); }}
                    />

                    <PaymentDetails
                        details={rawReservation}
                        tariff={estimates}
                        gotoPayments={gotoPayments}
                        unallocatedAmount={unallocatedAmount} // And pass it here
                        

                    />
                </Masonry>
            </CustomCard>
        </Box>
    );
}
